import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import ChecklistItem from 'src/components/CheckListItem/CheckListItem';
import { DEFAULT_MAX_VISIBLE_CARDS_COUNT } from 'src/util/constants';
import {
  calculateProgress,
  getChecklistItemState,
} from 'src/util/loading/parallelStageHelpers';
import buildLocalizedDateRangeLabel from 'src/util/time/dateRange';

import {
  PROPERTY_SHOW_LOADING_STATE_COMPLETE,
  PROPERTY_SHOW_LOADING_STATE_ERROR,
  PROPERTY_SHOW_LOADING_STATE_IDLE,
  PROPERTY_SHOW_LOADING_STATE_LOADING_CONSUMED,
  PROPERTY_SHOW_LOADING_STATE_LOADING_GENERATED,
  PROPERTY_SHOW_LOADING_STATE_LOADING_METERS,
  PROPERTY_SHOW_LOADING_STATE_LOADING_PROPERTY,
  PROPERTY_SHOW_LOADING_STATE_LOADING_RULES,
  PROPERTY_SHOW_LOADING_STATE_LOADING_TRADE_SUMMARY,
  PROPERTY_SHOW_LOADING_STATE_METERS_LOADED,
  PROPERTY_SHOW_LOADING_STATE_PREPARING_DATA,
  PROPERTY_SHOW_LOADING_STATE_RENDERING,
  PROPERTY_SHOW_LOADING_STATE_RULES_LOADED,
  PropertyShowLoadingStates,
  STAGE_PROGRESS,
  StageKeys,
} from '../reducer/constants';

// Define constants for checklist stage ordering
export const CHECKLIST_STATES = {
  PROPERTY: 1,
  METERS: 2,
  RULES: 3,
  CONSUMED_DATA: 4,
  GENERATED_DATA: 5,
  TRADE_SUMMARY: 6,
  DATA_PREPARED: 7,
  DATA_RENDERED: 8,
};

/**
 * Checklist items in the order they appear in the checklist.
 * Each item has an order for display and a stageKey for parallel stage tracking.
 * @type {{order: number, stageKey: string}[]}
 */
const CHECKLIST_ITEMS = Object.freeze([
  { order: CHECKLIST_STATES.PROPERTY, stageKey: StageKeys.PROPERTY },
  { order: CHECKLIST_STATES.METERS, stageKey: StageKeys.METERS },
  { order: CHECKLIST_STATES.RULES, stageKey: StageKeys.RULES },
  { order: CHECKLIST_STATES.CONSUMED_DATA, stageKey: StageKeys.CONSUMED_DATA },
  { order: CHECKLIST_STATES.GENERATED_DATA, stageKey: StageKeys.GENERATED_DATA },
  { order: CHECKLIST_STATES.TRADE_SUMMARY, stageKey: StageKeys.TRADE_SUMMARY },
  { order: CHECKLIST_STATES.DATA_PREPARED, stageKey: StageKeys.DATA_PREPARED },
  { order: CHECKLIST_STATES.DATA_RENDERED, stageKey: StageKeys.DATA_RENDERED },
]);

/**
 * Single source of truth for state metadata.
 * Maps each loading state to its checklist completion level.
 *
 * The `completedUpTo` property represents the highest checklist item order that should
 * be marked as COMPLETE when that loading state is active. This creates an intentional
 * "off by 1" pattern where the value indicates completed prerequisites rather than the
 * currently active stage.
 *
 * For example, when in LOADING_METERS state:
 * - completedUpTo = CHECKLIST_STATES.PROPERTY (1)
 * - This means: Property loading is COMPLETE ✓, Meters are ACTIVE ⟳
 *
 * This design allows the checklist UI to correctly show:
 * - Items with order <= completedUpTo as COMPLETE (✓)
 * - Item with order = completedUpTo + 1 as ACTIVE (⟳)
 * - Items with order > completedUpTo + 1 as INCOMPLETE (○)
 * @type {{[key:string]: {completedUpTo: number}}}
 */
export const STATE_METADATA = Object.freeze({
  [PROPERTY_SHOW_LOADING_STATE_IDLE]: { completedUpTo: 0 },
  [PROPERTY_SHOW_LOADING_STATE_LOADING_PROPERTY]: { completedUpTo: 0 },
  [PROPERTY_SHOW_LOADING_STATE_LOADING_METERS]: {
    completedUpTo: CHECKLIST_STATES.PROPERTY,
  },
  [PROPERTY_SHOW_LOADING_STATE_METERS_LOADED]: {
    completedUpTo: CHECKLIST_STATES.METERS,
  },
  [PROPERTY_SHOW_LOADING_STATE_LOADING_RULES]: {
    completedUpTo: CHECKLIST_STATES.METERS,
  },
  [PROPERTY_SHOW_LOADING_STATE_RULES_LOADED]: {
    completedUpTo: CHECKLIST_STATES.RULES,
  },
  [PROPERTY_SHOW_LOADING_STATE_LOADING_CONSUMED]: {
    completedUpTo: CHECKLIST_STATES.RULES,
  },
  [PROPERTY_SHOW_LOADING_STATE_LOADING_GENERATED]: {
    completedUpTo: CHECKLIST_STATES.CONSUMED_DATA,
  },
  [PROPERTY_SHOW_LOADING_STATE_LOADING_TRADE_SUMMARY]: {
    completedUpTo: CHECKLIST_STATES.GENERATED_DATA,
  },
  [PROPERTY_SHOW_LOADING_STATE_PREPARING_DATA]: {
    completedUpTo: CHECKLIST_STATES.TRADE_SUMMARY,
  },
  [PROPERTY_SHOW_LOADING_STATE_RENDERING]: {
    completedUpTo: CHECKLIST_STATES.DATA_PREPARED,
  },
  [PROPERTY_SHOW_LOADING_STATE_COMPLETE]: {
    completedUpTo: CHECKLIST_STATES.DATA_RENDERED,
  },
  [PROPERTY_SHOW_LOADING_STATE_ERROR]: { completedUpTo: 0 },
});

/**
 * Formats the appropriate label for a loading stage based on its order and completion status.
 * This function determines whether to show a "loading" or "loaded" message for each stage,
 * and includes dynamic count information for meters, rules, and trade summaries when available.
 * @param {object} params - The parameters object
 * @param {object} params.intl - The react-intl intl object for internationalization
 * @param {number} params.order - The stage order (1-8) representing which loading stage
 * to get the label for
 * @param {string[]} params.completedStages - Array of completed stage keys
 * @param {string} params.stageKey - The stage key for this item
 * @param {string} params.currentState - The current loading state string
 * @param {object} params.metadata - The metadata object containing counts (metersCount,
 * rulesCount, batchesLoaded, batchesTotal) for each stage
 * @returns {string|React.ReactElement} The label for the specified stage.
 * Returns a string for simple labels or a React element with formatted counts
 * and pluralization for stages with dynamic counts.
 */
const formatStageLabel = ({
  intl,
  order,
  completedStages,
  stageKey,
  currentState,
  metadata,
}) => {
  const {
    metersCount = 0, rulesCount = 0, batchesLoaded = 0, batchesTotal = 0,
  } = metadata || {};
  const completedStagesArr = completedStages || [];
  const isCompleted = completedStagesArr.includes(stageKey);

  switch (order) {
    case CHECKLIST_STATES.PROPERTY: {
      return isCompleted
        ? intl.formatMessage({
          id: 'property.property_show.helpers.build_loading_overlay_state.checklist.property_loaded.label',
          defaultMessage: 'Property loaded',
        })
        : intl.formatMessage({
          id: 'property.property_show.helpers.build_loading_overlay_state.checklist.loading_property.label',
          defaultMessage: 'Loading property',
        });
    }

    case CHECKLIST_STATES.METERS: {
      const isLoadingMetersWithCount = currentState === PropertyShowLoadingStates.LOADING_METERS
        && metersCount > 0;

      if (isCompleted || isLoadingMetersWithCount) {
        return intl.formatMessage(
          {
            id: 'property.property_show.helpers.build_loading_overlay_state.checklist.meters_loaded.label',
            defaultMessage: '{count, plural, one {<strong>#</strong> meter} other {<strong>#</strong> meters}} loaded',
          },
          { count: metersCount, strong: (chunks) => <strong>{chunks}</strong> },
        );
      }

      return intl.formatMessage({
        id: 'property.property_show.helpers.build_loading_overlay_state.checklist.loading_meters.label',
        defaultMessage: 'Loading meters',
      });
    }

    case CHECKLIST_STATES.RULES: {
      const isLoadingRulesWithCount = currentState === PropertyShowLoadingStates.LOADING_RULES
        && rulesCount > 0;

      if (isCompleted || isLoadingRulesWithCount) {
        return intl.formatMessage(
          {
            id: 'property.property_show.helpers.build_loading_overlay_state.checklist.rules_loaded.label',
            defaultMessage: '{count, plural, one {<strong>#</strong> trade rule} other {<strong>#</strong> trade rules}} loaded',
          },
          { count: rulesCount, strong: (chunks) => <strong>{chunks}</strong> },
        );
      }
      return intl.formatMessage({
        id: 'property.property_show.helpers.build_loading_overlay_state.checklist.loading_rules.label',
        defaultMessage: 'Loading trade rules',
      });
    }

    case CHECKLIST_STATES.CONSUMED_DATA: {
      return isCompleted
        ? intl.formatMessage({
          id: 'property.property_show.helpers.build_loading_overlay_state.checklist.consumed_data_loaded.label',
          defaultMessage: 'Consumed meter data loaded',
        })
        : intl.formatMessage({
          id: 'property.property_show.helpers.build_loading_overlay_state.checklist.loading_consumed_data.label',
          defaultMessage: 'Loading consumed meter data',
        });
    }

    case CHECKLIST_STATES.GENERATED_DATA: {
      return isCompleted
        ? intl.formatMessage({
          id: 'property.property_show.helpers.build_loading_overlay_state.checklist.generated_data_loaded.label',
          defaultMessage: 'Generated meter data loaded',
        })
        : intl.formatMessage({
          id: 'property.property_show.helpers.build_loading_overlay_state.checklist.loading_generated_data.label',
          defaultMessage: 'Loading generated meter data',
        });
    }

    case CHECKLIST_STATES.TRADE_SUMMARY: {
      const isLoadingTradeSummary = (
        currentState === PropertyShowLoadingStates.LOADING_TRADE_SUMMARY
      );

      if (isCompleted) {
        return intl.formatMessage(
          {
            id: 'property.property_show.helpers.build_loading_overlay_state.checklist.trade_summary_loaded.label',
            defaultMessage: '{count, plural, one {<strong>#</strong> trade summary} other {<strong>#</strong> trade summaries}} loaded',
          },
          { count: batchesTotal, strong: (chunks) => <strong>{chunks}</strong> },
        );
      }

      if (isLoadingTradeSummary && batchesTotal > 0) {
        return intl.formatMessage(
          {
            id: 'property.property_show.helpers.build_loading_overlay_state.checklist.loading_trade_summary_progress.label',
            defaultMessage: 'Loading {batchTotal, plural, one {trade summary} other {trade summaries}} ({batchLoaded} of {batchTotal})',
          },
          { batchLoaded: batchesLoaded, batchTotal: batchesTotal },
        );
      }

      return intl.formatMessage({
        id: 'property.property_show.helpers.build_loading_overlay_state.checklist.loading_trade_summary.label',
        defaultMessage: 'Loading trade summaries',
      });
    }

    case CHECKLIST_STATES.DATA_PREPARED: {
      return isCompleted
        ? intl.formatMessage({
          id: 'property.property_show.helpers.build_loading_overlay_state.checklist.data_prepared.label',
          defaultMessage: 'Data prepared',
        })
        : intl.formatMessage({
          id: 'property.property_show.helpers.build_loading_overlay_state.checklist.preparing_data.label',
          defaultMessage: 'Preparing data',
        });
    }

    case CHECKLIST_STATES.DATA_RENDERED: {
      return isCompleted
        ? intl.formatMessage({
          id: 'property.property_show.helpers.build_loading_overlay_state.checklist.data_rendered.label',
          defaultMessage: 'Data rendered',
        })
        : intl.formatMessage({
          id: 'property.property_show.helpers.build_loading_overlay_state.checklist.rendering_data.label',
          defaultMessage: 'Rendering data',
        });
    }

    default:
      return '';
  }
};

/**
 * Returns a checklist-style progress label showing all loading stages with dynamic messages.
 * Supports three visual states: completed (green), active (yellow), and pending (grey).
 * Multiple stages can be active simultaneously when requests are parallelized.
 * @param {object} params - The parameters object
 * @param {object} params.intl - The react-intl intl object
 * @param {string} params.currentState - The current loading state
 * @param {object} params.metadata - The metadata object containing counts for each stage
 * @param {string[]} params.activeStages - Currently active stage keys
 * @param {string[]} params.completedStages - Completed stage keys
 * @returns {React.ReactElement} A JSX element containing the checklist
 */
const displayChecklistProgress = ({
  intl, currentState, metadata, activeStages = [], completedStages = [],
}) => (
  <div>
    {CHECKLIST_ITEMS.map((item) => {
      const itemState = getChecklistItemState(item.stageKey, activeStages, completedStages);
      const label = formatStageLabel({
        intl,
        order: item.order,
        completedStages,
        stageKey: item.stageKey,
        currentState,
        metadata,
      });
      return (
        <ChecklistItem key={item.order} state={itemState} label={label} />
      );
    })}
  </div>
);

/**
 * Determines the grouping message based on the number of trade rules.
 * - If rulesCountForGrouping is falsy (null, 0, undefined): no message (empty string)
 * - If rulesCountForGrouping > 16: trade type grouping message (for performance)
 * - Otherwise: counterparty grouping message (default grouping)
 * @param {object} intl - The react-intl intl object.
 * @param {number} rulesCountForGrouping - The rules count for displaying grouping message.
 * @returns {string} The appropriate grouping message.
 */
const getGroupingMessage = (intl, rulesCountForGrouping) => {
  if (!rulesCountForGrouping) {
    return '';
  }

  if (rulesCountForGrouping > DEFAULT_MAX_VISIBLE_CARDS_COUNT) {
    return intl.formatMessage({
      id: 'property.property_show.helpers.build_loading_overlay_state.stage_6.grouping.trade_type.message',
      defaultMessage: 'To optimize the page performance, the data is grouped by trade type.',
    });
  }

  return intl.formatMessage({
    id: 'property.property_show.helpers.build_loading_overlay_state.stage_6.grouping.counterparty.message',
    defaultMessage: 'The data is grouped by counterparty.',
  });
};

/**
 * Generates loading overlay configuration based on the current loading state.
 * This function maps the PropertyShow loading state machine's current state to
 * the appropriate overlay configuration, providing user feedback during the
 * multi-stage data fetching pipeline. Each loading stage displays
 * a checklist showing all stages with checkboxes marked as completed.
 * @param {object} params - The parameters object.
 * @param {object} params.intl - The react-intl intl object for internationalization.
 * @param {object} params.datespan - Object with start and finish DateTime for date range display.
 * @param {string} params.timezone - Timezone for date formatting.
 * @param {object} params.loadingState - The current loading state from the reducer.
 * @param {boolean} params.showGroupingMessage - Whether to show the grouping message.
 * @param {Function} [params.onCancelFunc] - Optional callback to cancel the loading operation.
 * @returns {{ loadingOverlayProps: object|null }} Overlay configuration
 * object with `loadingOverlayProps` property containing the overlay settings, or null when
 * the loading state doesn't require an overlay.
 */
const buildLoadingOverlayState = ({
  intl,
  datespan,
  timezone,
  loadingState,
  showGroupingMessage,
  onCancelFunc,
}) => {
  if (!loadingState || !intl || !datespan) {
    return { loadingOverlayProps: null };
  }

  const {
    state, metadata, activeStages = [], completedStages = [],
  } = loadingState;

  // Header label with calendar icon
  const headerLabel = (
    <FormattedMessage
      id="property.property_show.helpers.build_loading_overlay_state.header.message"
      defaultMessage="{calenderIcon} Applying new date range"
      values={{
        calenderIcon: <FontAwesomeIcon icon={faCalendar} />,
      }}
    />
  );

  // Body label with date range
  const bodyLabel = intl.formatMessage({
    id: 'property.property_show.helpers.build_loading_overlay_state.body.message',
    defaultMessage: 'Updating to show {dateRange}:',
  }, {
    dateRange: buildLocalizedDateRangeLabel(
      intl,
      datespan.start,
      datespan.finish,
      timezone,
    ),
  });

  // Stage 6: Rendering (Preparing data and rendering)
  const footerLabel = showGroupingMessage
    ? getGroupingMessage(intl, metadata?.rulesCount)
    : '';

  // Build the checklist progress label with metadata and stage arrays
  const progressLabel = displayChecklistProgress({
    intl,
    currentState: state,
    metadata,
    activeStages,
    completedStages,
  });

  switch (state) {
    case PropertyShowLoadingStates.IDLE:
    case PropertyShowLoadingStates.ERROR:
    case PropertyShowLoadingStates.CANCELLED:
      return { loadingOverlayProps: null };
    case PropertyShowLoadingStates.LOADING_PROPERTY:
    case PropertyShowLoadingStates.LOADING_METERS:
    case PropertyShowLoadingStates.METERS_LOADED:
    case PropertyShowLoadingStates.LOADING_RULES:
    case PropertyShowLoadingStates.RULES_LOADED:
    case PropertyShowLoadingStates.LOADING_CONSUMED:
    case PropertyShowLoadingStates.LOADING_GENERATED:
    case PropertyShowLoadingStates.LOADING_TRADE_SUMMARY:
    case PropertyShowLoadingStates.PREPARING_DATA:
    case PropertyShowLoadingStates.RENDERING:
    case PropertyShowLoadingStates.COMPLETE:
      return {
        loadingOverlayProps: {
          headerLabel,
          bodyLabel,
          footerLabel,
          progressLabel,
          progressBarValue: calculateProgress(completedStages, STAGE_PROGRESS),
          updateProgress: () => {},
          onCancelFunc: onCancelFunc || null,
          cancelLabel: onCancelFunc
            ? intl.formatMessage({
              id: 'property.property_show.helpers.build_loading_overlay_state.cancel.label',
              defaultMessage: 'Cancel',
            })
            : null,
        },
      };
    default:
      return { loadingOverlayProps: null };
  }
};

export default buildLoadingOverlayState;
